import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { callTool, postCheckIn, postPresence } from "../api";
import {
  floorFiltersAreActive,
  useFloorFilterBoothIds,
  useRatingBoard,
  type CampaignFilters,
} from "../campaignFilters";
import { HallMap } from "../components/HallMap";
import { EVENT_KEY } from "../constants";
import { resolveMapHall } from "./mapHall";
import { buildWalkGraph } from "../geo/aisleRoute";
import {
  boothCentroidsFromHall,
  buildEuclideanTour,
  centroidForBooth,
  firstSwitchHall,
  hallKey,
  stitchTourPath,
  type TourStop,
} from "../geo/euclideanTour";
import { formatBoothHit, type BoothSearchHit } from "./boothLookup";
import { FloorDrawer } from "./FloorDrawer";
import { FloorFilterDrawer } from "./FloorFilterDrawer";
import { TeamActivityModal } from "./TeamActivityModal";
import { WorkMyPlan, workPlanSummary } from "./WorkMyPlan";
import type { TeamMember } from "./teamColors";
import type { CompanyBrowserTarget } from "../company-browser/types";
import type { CadPoint } from "../geo/gpsCadOffset";
import type { AisleGraph, HallBoothCollection, LoadState, PresenceRecord } from "../types";

export type FloorDestination = {
  booth_id: string;
  label: string;
  hall?: string;
};

function toLoadError(error: unknown, fallback: string): LoadState<never> {
  return {
    status: "error",
    message: error instanceof Error ? error.message : fallback,
  };
}

export function FloorWorkspace({
  eventKey,
  campaignKey,
  actor,
  signedInOid,
  signedInName,
  hallId,
  hallGeo,
  hallBooths,
  hallAisles,
  selectedBooth,
  originBooth,
  checkInQuery,
  onCheckInQuery,
  onOriginBooth,
  destination,
  goingToBooth,
  onGoingToBooth,
  onDestination,
  displayName,
  presence,
  presenceConnected,
  onHallChange,
  onSelectBooth,
  initialDrawerOpen = false,
  visitsEpoch = 0,
  searchPickRef,
  onOpenCompany,
  filters,
  onFiltersChange,
  filterDrawerOpen,
  onFilterDrawerOpenChange,
  sharedBoothIds,
}: {
  eventKey: string;
  campaignKey: string;
  actor: string;
  signedInOid?: string;
  signedInName: string;
  hallId: string;
  hallGeo: LoadState<HallBoothCollection>;
  hallBooths: HallBoothCollection | null;
  hallAisles: AisleGraph | null;
  selectedBooth: string;
  originBooth: string;
  checkInQuery: string;
  onCheckInQuery: (value: string) => void;
  onOriginBooth: (booth: string) => void;
  destination: FloorDestination | null;
  goingToBooth: string;
  onGoingToBooth: (value: string) => void;
  onDestination: (next: FloorDestination | null) => void;
  displayName: string;
  presence: PresenceRecord[];
  presenceConnected: boolean;
  onHallChange: (hall: string) => void;
  onSelectBooth: (booth: string) => void;
  initialDrawerOpen?: boolean;
  visitsEpoch?: number;
  searchPickRef?: MutableRefObject<((hit: BoothSearchHit) => void) | null>;
  onOpenCompany?: (target: CompanyBrowserTarget) => void;
  filters: CampaignFilters;
  onFiltersChange: (next: CampaignFilters) => void;
  filterDrawerOpen: boolean;
  onFilterDrawerOpenChange: (open: boolean) => void;
  sharedBoothIds?: ReadonlySet<string>;
}) {
  const [checkInState, setCheckInState] = useState<LoadState<PresenceRecord>>({ status: "idle" });
  const [drawerOpen, setDrawerOpen] = useState(initialDrawerOpen);
  const [closedAccountIds, setClosedAccountIds] = useState<Set<string>>(new Set());
  const [visitsTick, setVisitsTick] = useState(0);
  const [activityMember, setActivityMember] = useState<TeamMember | null>(null);
  const [gpsPin, setGpsPin] = useState<CadPoint | null>(null);
  const [tourPath, setTourPath] = useState<CadPoint[] | null>(null);
  const [tourBoothIds, setTourBoothIds] = useState<Set<string> | null>(null);
  const [tourOrder, setTourOrder] = useState<string[] | null>(null);
  const [routePending, setRoutePending] = useState(false);
  const ignoreMapSelectUntil = useRef(0);
  const pendingTourRef = useRef(false);
  const returnToWalkRef = useRef(false);
  const floorFilterOn = floorFiltersAreActive(filters);

  useEffect(() => {
    if (filterDrawerOpen) {
      setDrawerOpen(false);
      return;
    }
    if (!returnToWalkRef.current) return;
    returnToWalkRef.current = false;
    setDrawerOpen(true);
  }, [filterDrawerOpen]);
  const { rows, board } = useRatingBoard(
    eventKey,
    actor,
    filters,
    200,
    campaignKey,
    true,
    { workPlan: true, reloadKey: visitsEpoch + visitsTick },
  );
  const { boothIds: filterBoothIds, pending: filterBoothPending } = useFloorFilterBoothIds(
    eventKey,
    actor,
    filters,
    campaignKey,
  );
  const highlightPending = floorFilterOn && filterBoothPending;

  useEffect(() => {
    let cancelled = false;
    void callTool<{ results?: Array<{ account_id?: string | null }> }>("booth_visit_list", {
      event_key: eventKey,
      owner: actor,
      status: "visited",
      limit: 200,
    })
      .then((data) => {
        if (cancelled) return;
        const ids = new Set<string>();
        for (const row of data.results ?? []) {
          if (row.account_id) ids.add(row.account_id);
        }
        setClosedAccountIds(ids);
      })
      .catch(() => {
        if (!cancelled) setClosedAccountIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [eventKey, actor, visitsTick, visitsEpoch]);

  const unworked = useMemo(
    () => rows.filter((row) => !closedAccountIds.has(row.account_id)),
    [rows, closedAccountIds],
  );
  const closedCount = useMemo(
    () => rows.filter((row) => closedAccountIds.has(row.account_id)).length,
    [rows, closedAccountIds],
  );
  const planRows = useMemo(() => {
    if (!tourOrder?.length) return rows;
    const rank = new Map(tourOrder.map((id, index) => [id, index]));
    return rows.slice().sort((a, b) => {
      const ra = rank.get(a.account_id);
      const rb = rank.get(b.account_id);
      if (ra == null && rb == null) return 0;
      if (ra == null) return 1;
      if (rb == null) return -1;
      return ra - rb;
    });
  }, [rows, tourOrder]);

  useEffect(() => {
    setTourOrder(null);
    setTourPath(null);
    setTourBoothIds(null);
  }, [filters.favoritesOnly, filters.myRating, filters.minAi, filters.segment]);

  useEffect(() => {
    setTourOrder((prev) => {
      if (!prev) return prev;
      const live = new Set(unworked.map((row) => row.account_id));
      const next = prev.filter((id) => live.has(id));
      if (!next.length) return null;
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) return prev;
      return next;
    });
  }, [unworked]);

  const highlightedBoothIds = floorFilterOn ? filterBoothIds : undefined;

  const shareDestination = useCallback(
    async (next: FloorDestination | null) => {
      try {
        await postPresence({
          display_name: displayName.trim() || signedInName || "Team member",
          event_key: eventKey.trim() || EVENT_KEY,
          destination_booth_id: next?.booth_id ?? null,
          destination_label: next?.label ?? null,
          destination_hall: next?.hall ?? null,
        });
      } catch {
        // Presence is best-effort; local destination still drives the map.
      }
    },
    [displayName, eventKey, signedInName],
  );

  const setDestination = useCallback(
    (next: FloorDestination | null) => {
      onDestination(next);
      void shareDestination(next);
    },
    [onDestination, shareDestination],
  );

  const handleCheckInPick = useCallback(
    async (hit: BoothSearchHit) => {
      const eventId = eventKey.trim() || EVENT_KEY;
      onOriginBooth(hit.booth);
      onCheckInQuery(formatBoothHit(hit));
      const mapHall = resolveMapHall(hit.hall, hit.booth);
      if (mapHall && mapHall !== hallId) onHallChange(mapHall);
      setCheckInState({ status: "loading" });
      try {
        const record = await postCheckIn({
          user_id: signedInOid ?? "",
          display_name: displayName.trim() || signedInName || "Team member",
          event_key: eventId,
          hall_id: mapHall ?? hallId,
          booth_id: hit.booth,
        });
        setCheckInState({ status: "ready", data: record });
      } catch (error) {
        setCheckInState(toLoadError(error, "Check-in failed"));
      }
    },
    [displayName, eventKey, hallId, onCheckInQuery, onHallChange, onOriginBooth, signedInName, signedInOid],
  );

  const clearDestination = useCallback(() => {
    setTourPath(null);
    setTourBoothIds(null);
    setDestination(null);
    onGoingToBooth("");
  }, [onGoingToBooth, setDestination]);

  const clearAtAndRoute = useCallback(() => {
    onOriginBooth("");
    onCheckInQuery("");
    setTourPath(null);
    setTourBoothIds(null);
    setDestination(null);
    onGoingToBooth("");
    void postPresence({
      display_name: displayName.trim() || signedInName || "Team member",
      event_key: eventKey.trim() || EVENT_KEY,
      booth_id: null,
      destination_booth_id: null,
      destination_label: null,
      destination_hall: null,
    }).catch(() => undefined);
  }, [displayName, eventKey, onCheckInQuery, onGoingToBooth, onOriginBooth, setDestination, signedInName]);

  const applyGoingTo = useCallback(
    (hit: BoothSearchHit) => {
      const hall = resolveMapHall(hit.hall, hit.booth);
      setTourPath(null);
      setTourBoothIds(null);
      setDestination({ booth_id: hit.booth, label: hit.label, hall });
      onGoingToBooth(formatBoothHit({ booth: hit.booth, label: hit.label, hall }));
      onSelectBooth(hit.booth);
      if (hall && hall !== hallId) onHallChange(hall);
    },
    [hallId, onGoingToBooth, onHallChange, onSelectBooth, setDestination],
  );

  const routeAndCloseDrawer = useCallback(
    (hit: BoothSearchHit) => {
      ignoreMapSelectUntil.current = Date.now() + 600;
      applyGoingTo(hit);
      setDrawerOpen(false);
    },
    [applyGoingTo],
  );

  useEffect(() => {
    if (!searchPickRef) return;
    searchPickRef.current = routeAndCloseDrawer;
    return () => {
      searchPickRef.current = null;
    };
  }, [routeAndCloseDrawer, searchPickRef]);

  const handleSelectBooth = useCallback(
    (booth: string) => {
      if (Date.now() < ignoreMapSelectUntil.current) return;
      onSelectBooth(booth);
    },
    [onSelectBooth],
  );

  const routePlanToBooth = useCallback(
    (booth: string, label: string, hall?: string | null) => {
      routeAndCloseDrawer({ booth, label, hall: resolveMapHall(hall, booth) });
    },
    [routeAndCloseDrawer],
  );

  const handleGpsPin = useCallback(
    (point: CadPoint | null, geo?: { lat: number; lng: number; accuracyM?: number | null } | null) => {
      setGpsPin(point);
      void postPresence({
        display_name: displayName.trim() || signedInName || "Team member",
        event_key: eventKey.trim() || EVENT_KEY,
        hall_id: hallId,
        gps_xy: point ? [point.x, point.y] : null,
        gps_latlng: point && geo ? [geo.lat, geo.lng] : null,
        gps_accuracy_m: point && geo && geo.accuracyM != null ? geo.accuracyM : null,
      }).catch(() => undefined);
    },
    [displayName, eventKey, hallId, signedInName],
  );

  const paintTour = useCallback(() => {
    const centroids = boothCentroidsFromHall(hallBooths);
    const geometried: TourStop[] = [];
    const ungeometried: typeof unworked = [];
    for (const row of unworked) {
      const booth = row.booth?.trim() ?? "";
      const xy = booth ? centroidForBooth(centroids, booth) : null;
      if (!xy) {
        ungeometried.push(row);
        continue;
      }
      geometried.push({
        id: row.account_id,
        booth,
        hall: hallKey(resolveMapHall(row.hall, booth) ?? row.hall, hallId),
        x: xy.x,
        y: xy.y,
      });
    }
    const originCentroid = originBooth ? centroidForBooth(centroids, originBooth) : null;
    const originOnHall = Boolean(originCentroid);
    const originPoint = originOnHall ? gpsPin ?? originCentroid : null;
    const origin = originPoint
      ? { booth: originBooth || undefined, hall: hallId, x: originPoint.x, y: originPoint.y }
      : null;
    const orderedStops = buildEuclideanTour(geometried, { origin, currentHall: hallId, use2Opt: true });
    setTourOrder([...orderedStops.map((stop) => stop.id), ...ungeometried.map((row) => row.account_id)]);
    const path = stitchTourPath({
      booths: orderedStops.map((stop) => stop.booth),
      centroids,
      graph: buildWalkGraph(hallAisles),
      originBooth: originOnHall ? originBooth : null,
      originPoint,
    });
    setTourPath(path.length >= 2 ? path : null);
    setTourBoothIds(new Set(orderedStops.map((stop) => stop.booth)));
    const firstRow = orderedStops[0]
      ? unworked.find((row) => row.account_id === orderedStops[0]?.id)
      : ungeometried.find((row) => row.booth?.trim());
    if (firstRow?.booth) {
      const booth = firstRow.booth;
      const label = firstRow.name || booth;
      const hall = resolveMapHall(firstRow.hall, booth) ?? hallId;
      setDestination({ booth_id: booth, label, hall });
      onGoingToBooth(formatBoothHit({ booth, label, hall }));
    }
    setRoutePending(false);
  }, [gpsPin, hallAisles, hallBooths, hallId, onGoingToBooth, originBooth, setDestination, unworked]);

  const handleShowRoute = useCallback(() => {
    setTourPath(null);
    setTourBoothIds(null);
    setDestination(null);
    onGoingToBooth("");
    const centroids = boothCentroidsFromHall(hallBooths);
    const onHall = unworked.some((row) => {
      const booth = row.booth?.trim();
      return Boolean(booth && centroidForBooth(centroids, booth));
    });
    if (!onHall) {
      const nextHall = firstSwitchHall(
        unworked.map((row) => resolveMapHall(row.hall, row.booth) ?? row.hall),
        hallId,
      );
      if (nextHall) {
        pendingTourRef.current = true;
        setRoutePending(true);
        onHallChange(nextHall);
        return;
      }
    }
    paintTour();
  }, [hallBooths, hallId, onGoingToBooth, onHallChange, paintTour, setDestination, unworked]);

  useEffect(() => {
    if (!pendingTourRef.current) return;
    if (hallGeo.status === "loading") return;
    pendingTourRef.current = false;
    if (hallGeo.status === "ready" && hallBooths?.features.length) {
      paintTour();
      return;
    }
    setRoutePending(false);
  }, [hallBooths, hallGeo.status, hallId, paintTour]);

  return (
    <div className="floor-page">
      <div className={`floor-map-pane${drawerOpen ? " drawer-open" : " drawer-collapsed"}`}>
        <HallMap
          hallId={hallId}
          booths={hallBooths}
          aisles={hallAisles}
          geoState={hallGeo}
          selectedBooth={selectedBooth}
          originBooth={originBooth}
          destinationBooth={destination?.booth_id ?? null}
          tourPath={tourPath}
          tourBoothIds={tourBoothIds ?? undefined}
          highlightedBoothIds={highlightedBoothIds}
          sharedBoothIds={sharedBoothIds}
          highlightPending={highlightPending}
          presence={presence}
          selfUserId={signedInOid}
          selfEmail={actor}
          selfOnline={presenceConnected}
          onHallChange={onHallChange}
          onSelectBooth={handleSelectBooth}
          onGpsPin={handleGpsPin}
          onClearRoute={clearDestination}
          onOpenMember={setActivityMember}
          onOpenDrawer={() => {
            returnToWalkRef.current = false;
            onFilterDrawerOpenChange(false);
            setDrawerOpen(true);
          }}
          filtersActive={floorFiltersAreActive(filters)}
          planSummary={workPlanSummary(board, unworked.length, closedCount)}
        />
        {activityMember ? (
          <TeamActivityModal
            member={activityMember}
            presence={presence}
            eventKey={eventKey.trim() || EVENT_KEY}
            campaignKey={campaignKey}
            selfEmail={actor}
            selfUserId={signedInOid}
            selfGpsOn={Boolean(presence.find((entry) => entry.user_id === signedInOid)?.gps_on)}
            selfOnline={presenceConnected}
            onClose={() => setActivityMember(null)}
            onOpenAccount={(target) => onOpenCompany?.(target)}
          />
        ) : null}
        <FloorDrawer
          eventKey={eventKey}
          campaignKey={campaignKey}
          checkInQuery={checkInQuery}
          onCheckInQuery={onCheckInQuery}
          onCheckInPick={(hit) => {
            void handleCheckInPick(hit);
          }}
          checkInState={checkInState}
          goingToBooth={goingToBooth}
          onGoingToChange={onGoingToBooth}
          onGoingToPick={routeAndCloseDrawer}
          onClearAtAndRoute={clearAtAndRoute}
          destinationLabel={
            destination ? `${destination.booth_id} · ${destination.label}` : null
          }
          presence={presence}
          presenceConnected={presenceConnected}
          selfUserId={signedInOid}
          open={drawerOpen}
          onOpenChange={(open) => {
            if (open) {
              returnToWalkRef.current = false;
              onFilterDrawerOpenChange(false);
            }
            setDrawerOpen(open);
          }}
        >
          <WorkMyPlan
            eventKey={eventKey}
            actor={actor}
            rows={planRows}
            board={board}
            closedAccountIds={closedAccountIds}
            filters={filters}
            filterDrawerOpen={filterDrawerOpen}
            onOpenFilters={() => {
              returnToWalkRef.current = true;
              onFilterDrawerOpenChange(true);
            }}
            onShowRoute={handleShowRoute}
            routeBusy={routePending}
            onGoToBooth={routePlanToBooth}
            onClosedOut={() => {
              setTourPath(null);
              setTourBoothIds(null);
              setVisitsTick((value) => value + 1);
            }}
            onOpenCompany={onOpenCompany}
            sharedBoothIds={sharedBoothIds}
          />
        </FloorDrawer>
        <FloorFilterDrawer
          open={filterDrawerOpen}
          onOpenChange={(open) => {
            if (open) setDrawerOpen(false);
            onFilterDrawerOpenChange(open);
          }}
          filters={filters}
          onFiltersChange={onFiltersChange}
          matchCount={highlightedBoothIds?.size ?? 0}
          highlightPending={highlightPending}
        />
      </div>
    </div>
  );
}
